import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, CheckSquare, ChevronsLeft, ChevronsRight, FileText, Square } from "lucide-react";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import Modal from "../ui/Modal";

interface PdfPageSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageCount: number;
  file?: File | null;
  fileName?: string;
  initialSelectedPages?: number[];
  onConfirm: (selectedPages: number[]) => void;
}

const PAGE_SIZE = 24;
const MAX_SELECTED_PDF_PAGES = 30;

const buildAllPages = (pageCount: number) => Array.from({ length: pageCount }, (_, index) => index + 1);

export default function PdfPageSelectModal({
  isOpen,
  onClose,
  pageCount,
  file,
  fileName,
  initialSelectedPages = [],
  onConfirm,
}: PdfPageSelectModalProps) {
  const { showAlert } = useModal();
  const [currentPage, setCurrentPage] = useState(1);
  const [resolvedPageCount, setResolvedPageCount] = useState(pageCount || 0);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<number, string>>({});
  const [renderingPages, setRenderingPages] = useState<number[]>([]);
  const [failedPages, setFailedPages] = useState<number[]>([]);
  const [loadError, setLoadError] = useState("");
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    setCurrentPage(1);
    setSelectedPages([...initialSelectedPages].sort((a, b) => a - b));
    setResolvedPageCount(pageCount || 0);
  }, [initialSelectedPages, isOpen, pageCount]);

  useEffect(() => {
    if (!isOpen || !file) {
      requestIdRef.current += 1;
      setResolvedPageCount(pageCount || 0);
      setThumbnails({});
      setRenderingPages([]);
      setFailedPages([]);
      setLoadError("");
      return;
    }

    setResolvedPageCount(pageCount || 0);
    setThumbnails({});
    setRenderingPages([]);
    setFailedPages([]);
    setLoadError("");
  }, [file, isOpen]);

  const totalPaginationPages = Math.max(1, Math.ceil(resolvedPageCount / PAGE_SIZE));
  const pageNumbers = useMemo(() => buildAllPages(resolvedPageCount), [resolvedPageCount]);
  const visiblePages = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return pageNumbers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, pageNumbers]);

  const visiblePageSet = useMemo(() => new Set(visiblePages), [visiblePages]);
  const selectedPageSet = useMemo(() => new Set(selectedPages), [selectedPages]);
  const visibleSelectedCount = visiblePages.filter((page) => selectedPageSet.has(page)).length;

  const showSelectionLimitAlert = async () => {
    await showAlert({
      title: "Sayfa sınırı aşıldı",
      message: `PDF'ten aynı anda en fazla ${MAX_SELECTED_PDF_PAGES} sayfa seçebilirsiniz.`,
      type: "warning",
    });
  };

  const applySelectedPages = (pages: number[]) => {
    const normalizedPages = [...new Set(pages)].sort((left, right) => left - right);

    if (normalizedPages.length > MAX_SELECTED_PDF_PAGES) {
      void showSelectionLimitAlert();
      return;
    }

    setSelectedPages(normalizedPages);
  };

  useEffect(() => {
    if (!isOpen || !file || !visiblePages.length) return;

    const missingPages = visiblePages.filter(
      (pageNumber) => !thumbnails[pageNumber] && !renderingPages.includes(pageNumber)
    );
    if (!missingPages.length) return;

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadError("");
    setRenderingPages((prev) => [...new Set([...prev, ...missingPages])]);
    setFailedPages((prev) => prev.filter((pageNumber) => !missingPages.includes(pageNumber)));

    const loadPreviews = async () => {
      try {
        const result = await ApiService.getMaarifQuestionPdfPreviews(file, missingPages);

        if (requestIdRef.current !== requestId) return;

        const previewMap = result.previews.reduce<Record<number, string>>((acc, item) => {
          acc[item.pageNumber] = item.dataUrl;
          return acc;
        }, {});

        setThumbnails((prev) => ({ ...prev, ...previewMap }));

        const failed = missingPages.filter((pageNumber) => !previewMap[pageNumber]);
        if (failed.length) {
          setFailedPages((prev) => [...new Set([...prev, ...failed])]);
        }
      } catch (error) {
        if (requestIdRef.current !== requestId) return;

        setFailedPages((prev) => [...new Set([...prev, ...missingPages])]);
        setLoadError(error instanceof Error ? error.message : "PDF önizlemeleri hazırlanamadı.");
      } finally {
        if (requestIdRef.current !== requestId) return;

        setRenderingPages((prev) => prev.filter((pageNumber) => !missingPages.includes(pageNumber)));
      }
    };

    void loadPreviews();
  }, [file, isOpen, renderingPages, thumbnails, visiblePages]);

  const togglePage = (pageNumber: number) => {
    applySelectedPages(
      selectedPages.includes(pageNumber)
        ? selectedPages.filter((item) => item !== pageNumber)
        : [...selectedPages, pageNumber]
    );
  };

  const setAllPagesSelected = () => applySelectedPages(buildAllPages(resolvedPageCount));
  const clearAllPages = () => applySelectedPages([]);

  const selectVisiblePages = () => {
    applySelectedPages([...selectedPages, ...visiblePages]);
  };

  const clearVisiblePages = () => {
    applySelectedPages(selectedPages.filter((page) => !visiblePageSet.has(page)));
  };

  const handleConfirm = () => {
    if (!selectedPages.length) return;
    if (selectedPages.length > MAX_SELECTED_PDF_PAGES) {
      void showSelectionLimitAlert();
      return;
    }
    onConfirm([...selectedPages].sort((a, b) => a - b));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="PDF Sayfa Seçimi" size="xl">
      <div className="space-y-5">
        <div className="rounded-2xl border border-sky-200 bg-sky-50/60 px-4 py-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-sky-900">
                <FileText className="h-4 w-4" />
                {fileName || file?.name || "PDF dosyası"}
              </div>
              <div className="mt-1 text-sm text-sky-900/80">
                {resolvedPageCount} sayfa var. Modelle yalnız seçtiğiniz sayfalar paylaşılacak.
              </div>
            </div>
            <div className="rounded-xl border border-sky-100 bg-white px-4 py-3 text-sm text-gray-700">
              <div>
                Seçili: <span className="font-semibold">{selectedPages.length}</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">Bu ekranda seçili: {visibleSelectedCount}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={setAllPagesSelected}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <CheckSquare className="h-4 w-4" />
            Tümünü Seç
          </button>
          <button
            onClick={clearAllPages}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Square className="h-4 w-4" />
            Tümünü Temizle
          </button>
          <button
            onClick={selectVisiblePages}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Bu Ekrandakileri Seç
          </button>
          <button
            onClick={clearVisiblePages}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Bu Ekrandakileri Temizle
          </button>
        </div>

        {loadError ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{loadError}</div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {visiblePages.map((pageNumber) => {
            const isSelected = selectedPageSet.has(pageNumber);
            const thumbnailSrc = thumbnails[pageNumber];
            const isRendering = renderingPages.includes(pageNumber);
            const isFailed = failedPages.includes(pageNumber);

            return (
              <button
                key={pageNumber}
                onClick={() => togglePage(pageNumber)}
                className={`rounded-2xl border p-3 text-left transition ${
                  isSelected
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  {thumbnailSrc ? (
                    <img
                      src={thumbnailSrc}
                      alt={`PDF sayfa ${pageNumber} önizleme`}
                      className="h-40 w-full object-cover object-top"
                    />
                  ) : (
                    <div className="flex h-40 items-center justify-center bg-gray-100 px-3 text-center text-xs text-gray-500">
                      {isFailed
                        ? "Önizleme alınamadı"
                        : isRendering
                          ? "Yükleniyor..."
                          : "Önizleme hazırlanıyor"}
                    </div>
                  )}
                </div>
                <div className="mt-3 text-xs uppercase tracking-wide opacity-70">Sayfa</div>
                <div className="mt-1 text-xl font-semibold">{pageNumber}</div>
                <div className="mt-2 text-xs font-medium">{isSelected ? "Seçildi" : "Hariç"}</div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 disabled:opacity-50"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <span>
              Sayfa {currentPage} / {totalPaginationPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPaginationPages, prev + 1))}
              disabled={currentPage === totalPaginationPages}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 disabled:opacity-50"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Vazgeç
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedPages.length}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              Seçimi Kaydet
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
